/**
 * PaymentCallback — Paystack redirects the customer here with a `reference`
 * query param after they complete (or cancel) checkout. We verify server-side
 * and redirect to the tracking page on success.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';

const PaymentCallback = () => {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { restoreCartBackup } = useApp();
  const [state, setState] = useState('verifying'); // verifying | success | failed
  const [message, setMessage] = useState('Verifying your payment…');

  useEffect(() => {
    const reference = params.get('reference') || params.get('trxref');
    const orderId = params.get('order_id');
    const provider = params.get('provider') || 'paystack';
    if (!reference) {
      setState('failed');
      setMessage('Missing payment reference in the URL.');
      return;
    }
    (async () => {
      try {
        const path = provider === 'paystack'
          ? `/paystack/verify/${reference}`
          : `/payments/status/${reference}`;
        const { data } = await api.get(path);
        if (data.payment_status === 'paid') {
          setState('success');
          setMessage('Payment confirmed. Taking you to your order…');
          toast.success('Payment received!');
          setTimeout(() => nav(`/track/${data.order_id || orderId}`, { replace: true }), 1200);
        } else {
          setState('failed');
          setMessage(`Payment status: ${data.status || data.payment_status || 'unknown'}. If you were charged, contact support.`);
        }
      } catch (e) {
        setState('failed');
        setMessage(e?.response?.data?.detail || 'Could not verify payment. Please contact support.');
      }
    })();
  }, []);

  const Icon = state === 'success' ? CheckCircle2 : state === 'failed' ? XCircle : Loader2;
  const color = state === 'success'
    ? 'text-emerald-600'
    : state === 'failed' ? 'text-rose-600' : 'text-slate-500';

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="payment-callback">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm">
        <Icon className={`h-14 w-14 mx-auto ${color} ${state === 'verifying' ? 'animate-spin' : ''}`} />
        <h1 className="mt-4 text-xl font-semibold text-slate-900">
          {state === 'success' && 'Payment successful'}
          {state === 'failed' && 'Payment not completed'}
          {state === 'verifying' && 'Verifying payment'}
        </h1>
        <p className="mt-2 text-sm text-slate-500">{message}</p>
        {state === 'failed' && (
          <div className="mt-6 flex flex-col gap-2">
            <button
              onClick={() => { const ok = restoreCartBackup(); nav(ok ? '/checkout' : '/cart'); }}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              data-testid="payment-retry-btn"
            >
              Restore basket & try again
            </button>
            <button
              onClick={() => nav('/orders')}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-slate-900 text-white rounded-lg hover:bg-slate-800"
              data-testid="payment-callback-back"
            >
              View my orders
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentCallback;
